import { Link } from 'react-router-dom'
import { CheckCheck, Megaphone } from 'lucide-react'
import { NotificationList } from './NotificationList'
import type { UserNotification } from '../types'
import { useAuthStore } from '@/stores/authStore'
import { PLATFORM_ROLES } from '@/types/auth'
import { Timestamp } from '@/components/ui/Timestamp'
import type { PlatformMessage } from '@/features/notification-channels/types'

// Severity dot for a broadcast row (the colour is paired with the severity word for screen readers).
const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  critical: 'bg-red-600',
}

interface NotificationPanelProps {
  notifications: UserNotification[]
  isLoading: boolean
  unreadCount: number
  onMarkAsRead: (id: string) => void
  onMarkAllAsRead: () => void
  onDelete: (id: string) => void
  onNotificationClick?: (notification: UserNotification) => void
  onClose?: () => void
  /** Unread platform broadcasts to show at the top (newest first, already limited). */
  broadcasts?: PlatformMessage[]
  /** How many unread broadcasts there are in all, which may be more than are listed. */
  broadcastUnreadTotal?: number
  /** Every broadcast this tenant has, read or not. Any at all keeps a path to them in the footer. */
  broadcastTotal?: number
  onBroadcastClick?: (message: PlatformMessage) => void
}

export function NotificationPanel({
  notifications,
  isLoading,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onNotificationClick,
  onClose,
  broadcasts = [],
  broadcastUnreadTotal = broadcasts.length,
  broadcastTotal = 0,
  onBroadcastClick,
}: NotificationPanelProps) {
  // Route "View all" to the active scope. The notifications page is mounted at
  // /tenant/notifications and /platform/notifications-inbox — a bare /notifications
  // is a dead route (404). A user acting inside a tenant reads the tenant page;
  // the platform console (no active tenant + platform role) reads the platform
  // inbox. LIVE-25: this used to point at /platform/notifications, which is the
  // platform_admin-only BROADCAST COMPOSER (navigation.ts's "Broadcasts" nav
  // item, App.tsx) — not an inbox of the admin's own alerts, and 404s outright
  // for platform_operator/platform_viewer (that route is RequireRole
  // platform_admin-gated). /platform/notifications-inbox is the real inbox,
  // reusing the tenant page's "My Feed" tab/hooks, open to every role this
  // isPlatformConsole check covers.
  //
  // Do NOT "keep this list in sync" with App.tsx's PLATFORM_ROLES or with
  // usernotification/handler.go's isPlatformUser. An earlier version of this
  // comment said to, and following it is what widened the Go allowlist to the
  // whole platform plane -- admitting platform_support_l1/l2, which hold no
  // "audit" grant, to cross-tenant audit data. The three serve different jobs:
  // PLATFORM_ROLES decides who may open the route, isPlatformUser decides whose
  // data the route may read, and this check decides where "View all" navigates.
  // Widen any of them against role_permissions, not against each other.
  // (platform_auditor is here because App.tsx's route guard already let an
  // auditor reach /platform/notifications-inbox, so "View all" silently sent
  // them to the tenant page instead -- review round 2 nit.)
  const user = useAuthStore((s) => s.user)
  // Derived from the same PLATFORM_ROLES App.tsx routes on, so the "View all"
  // destination and the route guard can no longer disagree. The hand-written
  // four-role list this replaces had already drifted once (platform_auditor was
  // missing); it would have drifted again for the 15 ops/support/finance roles.
  const isPlatformConsole = !user?.tenantId && !!user?.role && PLATFORM_ROLES.includes(user.role)
  const viewAllPath = isPlatformConsole ? '/platform/notifications-inbox' : '/tenant/notifications'

  const handleNotificationClick = (notification: UserNotification) => {
    if (onNotificationClick) {
      onNotificationClick(notification)
    }
    // Navigate to action URL if present
    if (notification.action_url && onClose) {
      onClose()
    }
  }

  return (
    <div className="w-96 max-h-[480px] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({unreadCount} unread)
            </span>
          )}
        </h3>
        {unreadCount > 0 && (
          <button
            onClick={onMarkAllAsRead}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </button>
        )}
      </div>

      {/* Notification list. overscroll-contain stops the scroll from CHAINING to the
          page behind the dropdown: without it, scrolling past the end of this list
          scrolls the app-main region, so the whole page slides up and dark empty space
          appears below it. */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {broadcasts.length > 0 && (
          <section aria-label="Broadcasts" className="border-b border-border">
            <h4 className="flex items-center gap-1.5 px-4 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Megaphone className="h-3.5 w-3.5" aria-hidden="true" />
              Broadcasts
              <span className="font-normal normal-case tracking-normal">({broadcastUnreadTotal} unread)</span>
            </h4>
            <ul className="py-1">
              {broadcasts.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => onBroadcastClick?.(m)}
                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-surface-2"
                  >
                    <span
                      aria-hidden="true"
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[m.severity] ?? SEVERITY_DOT.info}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground">
                        <span className="sr-only">{m.severity}: </span>
                        {m.title}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        <Timestamp value={m.published_at} format="relative" />
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <NotificationList
          notifications={notifications}
          isLoading={isLoading}
          onMarkAsRead={onMarkAsRead}
          onDelete={onDelete}
          onNotificationClick={handleNotificationClick}
        />
      </div>

      {/* Footer: shown with many notifications, or whenever there are broadcasts. The Notifications
          page has no sidebar entry, so this is the only way to reach older or already-read broadcasts. */}
      {(notifications.length >= 10 || broadcastTotal > 0) && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-border bg-surface-2">
          <Link
            to={viewAllPath}
            onClick={onClose}
            className="text-sm text-primary-700 hover:text-primary-800"
          >
            View all notifications
          </Link>
          {broadcastTotal > 0 && (
            <Link
              to={`${viewAllPath}?tab=broadcasts`}
              onClick={onClose}
              className="text-sm text-primary-700 hover:text-primary-800"
            >
              All broadcasts
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
